/**
 * Utilidades visuales para RUT chileno.
 *
 * Convención del proyecto: el API guarda el RUT en formato canónico
 * (solo dígitos + DV en mayúscula, sin puntos ni guión). El formato
 * "12.345.678-9" se aplica solo en la capa de presentación.
 */

export function cleanRut(rut: string | null | undefined): string {
  if (!rut) return "";
  return rut.replace(/[^0-9kK]/g, "").toUpperCase();
}

/**
 * Formatea para mostrar: "123456789" → "12.345.678-9".
 * Si el input es muy corto o vacío devuelve el clean tal cual.
 */
export function formatRut(rut: string | null | undefined): string {
  const clean = cleanRut(rut);
  if (clean.length < 2) return clean;

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${formattedBody}-${dv}`;
}

/**
 * Calcula el dígito verificador esperado para el cuerpo numérico del RUT
 * (módulo 11). Acepta el RUT entero o solo el cuerpo: ignora el último
 * carácter si parece DV.
 */
export function calculateDv(rut: string | null | undefined): string {
  const clean = cleanRut(rut);
  const body = clean.length > 1 ? clean.slice(0, -1) : clean;
  if (!/^\d+$/.test(body)) return "";

  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body.charAt(i), 10) * multiplier;
    multiplier = multiplier < 7 ? multiplier + 1 : 2;
  }

  const remainder = 11 - (sum % 11);
  if (remainder === 11) return "0";
  if (remainder === 10) return "K";
  return remainder.toString();
}

/**
 * Valida un RUT chileno comparando el dígito verificador ingresado contra
 * el calculado por módulo 11. Devuelve `false` para vacío, longitudes
 * inválidas o cuerpos no numéricos.
 */
export function validateRut(rut: string | null | undefined): boolean {
  const clean = cleanRut(rut);
  if (clean.length < 2) return false;

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);

  if (!/^\d+$/.test(body)) return false;
  if (!/^[0-9K]$/.test(dv)) return false;

  return dv === calculateDv(clean);
}
